import type { ReactNode } from "react";
import { AppWindow, Check, Folder, FolderCode, FolderInput, FolderMinus, FolderPlus, Layers, Link2, MessageSquare, Pause, Pencil, Pin, PinOff, Play, RotateCcw, Square, Zap } from "lucide-react";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { useAgentFolders, useFolderActions } from "@/hooks/use-agent-folders";
import type { useStopAgent } from "@/components/stop-agent-button";
import { api } from "@/lib/api";
import { useCan } from "@/lib/auth";
import type { AgentWithContext } from "@/lib/types";

/**
 * "Move to folder ▸": the way to arrange the list without dragging — from a
 * keyboard, a desktop, or the chat's ⋯. "New folder…" makes one of this agent
 * alone and opens its name in the list for editing.
 */
function MoveToFolderItem({ agent, disabled }: { agent: AgentWithContext; disabled: boolean }) {
  const folders = useAgentFolders();
  const { moveTo, createWith } = useFolderActions();
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger disabled={disabled}>
        <FolderInput /> Move to folder
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-52">
        {folders.map((folder) => (
          <DropdownMenuItem key={folder.id} onSelect={() => folder.id !== agent.folderId && moveTo(agent.id, folder.id)}>
            <Folder /> <span className="min-w-0 flex-1 truncate">{folder.name}</span>
            {folder.id === agent.folderId ? <Check className="ml-auto" /> : null}
          </DropdownMenuItem>
        ))}
        {folders.length > 0 ? <DropdownMenuSeparator /> : null}
        <DropdownMenuItem onSelect={() => createWith([agent])}>
          <FolderPlus /> New folder…
        </DropdownMenuItem>
        {agent.folderId ? (
          <DropdownMenuItem onSelect={() => moveTo(agent.id, null)}>
            <FolderMinus /> Remove from folder
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

/** Runs one action and reports a failure in the caller's own way; `onChanged` after success is its job. */
export type AgentAct = (label: string, fn: () => Promise<unknown>) => Promise<void>;

/**
 * Everything that can be done to an agent, as menu items: the chat header's ⋯ and
 * the list row's hold / right-click menu render this one list, so the two cannot
 * drift. Pass it inside a `DropdownMenuContent`. Driving an agent is admin work
 * (the server refuses it below that); the master is never pinned, because it is
 * always first.
 */
export function AgentActionItems({
  agent,
  queued,
  stopControl,
  busy,
  act,
  onOpen,
  onOpenBeside,
  onTriggers,
  onEdit,
  onOpenProject,
  chatItems,
  onPair,
}: {
  agent: AgentWithContext;
  /** Turns waiting behind the run: "Stop and clear the queue" is offered only with some. */
  queued: number;
  stopControl: ReturnType<typeof useStopAgent>;
  busy: boolean;
  act: AgentAct;
  /** The row's menu leads with what a tap does. */
  onOpen?: () => void;
  /** Beside it, where the list is a desktop window: what Ctrl/Cmd-click does. */
  onOpenBeside?: () => void;
  onTriggers: () => void;
  onEdit: () => void;
  /** Opens the agent's project on the Projects page. Absent where there is no page to go to. */
  onOpenProject?: (projectId: string) => void;
  /** Items that only make sense in the open chat (dictation), placed above pin/triggers/edit. */
  chatItems?: ReactNode;
  /** A foreign agent: open the pairing screen with a fresh code. */
  onPair?: () => void;
}) {
  const canDrive = useCan("admin");
  const working = agent.status === "working";
  return (
    <>
      {onOpen ? (
        <>
          <DropdownMenuItem onSelect={onOpen}>
            <MessageSquare /> Open chat
          </DropdownMenuItem>
          {onOpenBeside ? (
            <DropdownMenuItem onSelect={onOpenBeside}>
              <AppWindow /> Open in new window
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
        </>
      ) : null}
      {canDrive ? (
        agent.status === "stopped" ? (
          <DropdownMenuItem disabled={busy} onSelect={() => void act("resume", () => api.resumeAgent(agent.id))}>
            <Play /> Resume
            <span className="ml-auto text-[11px] text-muted-foreground">queued start</span>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem disabled={busy} onSelect={() => void act("pause", () => api.pauseAgent(agent.id))}>
            <Pause /> Pause
            <span className="ml-auto text-[11px] text-muted-foreground">{working ? "stops this run" : "holds the queue"}</span>
          </DropdownMenuItem>
        )
      ) : null}
      {canDrive && working ? (
        <>
          <DropdownMenuItem disabled={stopControl.stopping} onSelect={() => void stopControl.stop(false)}>
            <Square /> {stopControl.stopping ? "Stopping…" : "Stop this run"}
          </DropdownMenuItem>
          {queued > 0 ? (
            <DropdownMenuItem destructive disabled={stopControl.stopping} onSelect={() => void stopControl.stop(true)}>
              <Square /> Stop and clear the queue ({queued})
            </DropdownMenuItem>
          ) : null}
        </>
      ) : null}
      {canDrive ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={busy || working || !agent.sessionId}
            onSelect={() => {
              if (window.confirm(`Summarise ${agent.name}'s conversation now and continue from the summary? The chat keeps every message.`)) {
                void act("compact", () => api.compactAgent(agent.id));
              }
            }}
          >
            <Layers /> Compact context
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={busy || working}
            onSelect={() => {
              if (window.confirm(`Start ${agent.name} on a fresh session? It forgets this conversation; the transcript stays here.`)) {
                void act("reset", () => api.resetAgent(agent.id));
              }
            }}
          >
            <RotateCcw /> Reset session
          </DropdownMenuItem>
        </>
      ) : null}
      {chatItems}
      <DropdownMenuSeparator />
      {canDrive && agent.kind !== "master" ? (
        <DropdownMenuItem
          disabled={busy}
          onSelect={() => void act(agent.pinned ? "unpin" : "pin", () => api.updateAgent(agent.id, { pinned: !agent.pinned }))}
        >
          {agent.pinned ? <PinOff /> : <Pin />} {agent.pinned ? "Unpin from the top" : "Pin to the top"}
        </DropdownMenuItem>
      ) : null}
      {canDrive && agent.kind !== "master" ? <MoveToFolderItem agent={agent} disabled={busy} /> : null}
      {onOpenProject && agent.project ? (
        <DropdownMenuItem onSelect={() => onOpenProject(agent.project!.id)}>
          <FolderCode /> Open project
        </DropdownMenuItem>
      ) : null}
      {canDrive && onPair ? (
        <DropdownMenuItem onSelect={onPair}>
          <Link2 /> {agent.link?.paired ? "Pair again…" : "Pair with a machine…"}
          <span className="ml-auto text-[11px] text-muted-foreground">new code</span>
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem onSelect={onTriggers}>
        <Zap /> Triggers
      </DropdownMenuItem>
      {canDrive ? (
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil /> Edit agent
        </DropdownMenuItem>
      ) : null}
    </>
  );
}
